import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  BlogActions,
  BlogActionTypes,
  CreateBlogTagFailed,
  CreateBlogTagSuccessful,
  CreateListingFailed,
  CreateListingSuccessful,
  DeleteBlogListingItemFailed,
  DeleteBlogListingItemSuccessful,
  LoadBlogCategoriesFailed,
  LoadBlogCategoriesSuccessful,
  LoadBlogListingFailed,
  LoadBlogListingSuccessful,
  LoadBlogsFailed,
  LoadBlogsSuccessful,
  LoadBlogTagsFailed,
  LoadBlogTagsSuccessful,
  UpdateListingFailed,
  UpdateListingSuccesful,
} from '../actions/blog.actions';
import { BlogService } from '../../lib/services/blogService';
import { Page } from '../../lib/classes/page';
import { State } from '../reducers/blog.reducer';
import { select, Store } from '@ngrx/store';
import { getBlogState } from '../reducers';
import { ListingService } from '../../jobs-module/services/listing.service';
import { BlogListingModel } from '../models/blog-listing.model';
import { BlogCategory, BlogTag } from '../models/blog-post.model';
import { Router } from '@angular/router';

@Injectable()
export class BlogEffects {
  @Effect()
  loadBlogs$ = this.actions$.pipe(
    ofType(BlogActionTypes.LoadBlogs),
    switchMap(action =>
      this.blogService.getBlogListings(action.payload.unit_id).pipe(
        map((response: Page<BlogListingModel>) => {
          return new LoadBlogsSuccessful({
            blog_listings: response.results,
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(new LoadBlogsFailed());
        })
      )
    )
  );

  @Effect()
  deleteBlogListingItem$ = this.actions$.pipe(
    ofType(BlogActionTypes.DeleteBlogListingItem),
    switchMap(action =>
      this.listingService.deleteListingItem(action.payload.item).pipe(
        map(res => {
          return new DeleteBlogListingItemSuccessful();
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(new DeleteBlogListingItemFailed());
        })
      )
    )
  );

  @Effect()
  loadBlogCategories$ = this.actions$.pipe(
    ofType(BlogActionTypes.LoadBlogCategories),
    switchMap(action =>
      this.blogService.getBlogCategories().pipe(
        map((response: Page<BlogCategory>) => {
          return new LoadBlogCategoriesSuccessful({
            categories: response.results,
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(new LoadBlogCategoriesFailed());
        })
      )
    )
  );

  @Effect()
  loadBlogTags$ = this.actions$.pipe(
    ofType(BlogActionTypes.LoadBlogTags),
    switchMap(action =>
      this.blogService.getBlogTags().pipe(
        map((response: Page<BlogTag>) => {
          return new LoadBlogTagsSuccessful({
            tags: response.results,
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(new LoadBlogTagsFailed());
        })
      )
    )
  );

  @Effect()
  createBlogTags$ = this.actions$.pipe(
    ofType(BlogActionTypes.CreateBlogTag),
    withLatestFrom(this.store$.pipe(select(getBlogState))),
    switchMap(([action, state]) =>
      this.blogService.createBlogTag(action.payload.tag).pipe(
        map((response: BlogTag) => {
          const all_tags = state.blog_tags.slice();
          all_tags.push(response);

          const new_listing = state.selected_listing;
          const listing_tags = new_listing.blog_post.tags.slice();
          listing_tags.push(response);
          new_listing.blog_post.tags = listing_tags;

          const is_valid_blog_info_created_tag = this.blogService.checkValidBlogInfo(
            new_listing.blog_post
          );

          return new CreateBlogTagSuccessful({
            blog_tags: all_tags,
            selected_listing: new_listing,
            is_blog_info_valid: is_valid_blog_info_created_tag,
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(new CreateBlogTagFailed());
        })
      )
    )
  );

  @Effect()
  createBlogListing = this.actions$.pipe(
    ofType(BlogActionTypes.CreateListing),
    withLatestFrom(this.store$.pipe(select(getBlogState))),
    switchMap(([action, state]) =>
      this.blogService.createBlogListing(action.payload.listing).pipe(
        map((response: BlogListingModel) => {
          const new_listing_list = state.blog_listing_list;
          new_listing_list.push(action.payload.listing);
          return new CreateListingSuccessful({
            listings: new_listing_list,
            new_listing: this.blogService.createEmptyBlogListing(),
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(
            new CreateListingFailed({
              error:
                Object.keys(errors.error)[0] +
                ': ' +
                errors.error[Object.keys(errors.error)[0]],
            })
          );
        })
      )
    )
  );

  @Effect()
  updateBlogListing = this.actions$.pipe(
    ofType(BlogActionTypes.UpdateListing),
    withLatestFrom(this.store$.pipe(select(getBlogState))),
    switchMap(([action, state]) =>
      this.blogService.updateBlogListing(action.payload.listing).pipe(
        map((response: BlogListingModel) => {
          const new_listing_list = state.blog_listing_list;
          const listing_index = new_listing_list.findIndex(
            item => item.id === response.id
          );
          new_listing_list[listing_index] = response;
          return new UpdateListingSuccesful({
            listings: new_listing_list,
            new_listing: this.blogService.createEmptyBlogListing(),
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          return of(
            new UpdateListingFailed({
              error:
                Object.keys(errors.error)[0] +
                ': ' +
                errors.error[Object.keys(errors.error)[0]],
            })
          );
        })
      )
    )
  );

  @Effect()
  loadBlogListing$ = this.actions$.pipe(
    ofType(BlogActionTypes.LoadBlogListing),
    switchMap(action =>
      this.blogService.getBlogListing(action.payload.id).pipe(
        map((response: BlogListingModel) => {
          return new LoadBlogListingSuccessful({
            new_listing: response,
            is_blog_info_valid: this.blogService.checkValidBlogInfo(
              response.blog_post
            ),
            is_valid_blog_listing: this.blogService.checkValidBlogListing(
              response
            ),
          });
        }),
        catchError(errors => {
          console.error('fail?', errors);
          this._router.navigate(['dashboard', 'blog']);
          return of(
            new LoadBlogListingFailed({
              error: errors,
            })
          );
        })
      )
    )
  );

  constructor(
    private actions$: Actions<BlogActions>,
    private listingService: ListingService,
    private blogService: BlogService,
    private store$: Store<State>,
    private _router: Router
  ) {}
}
